// forwardedSmsApi — client access to the forwarded-SMS log.
//
// forwardedSms has no client read/write rule (written by the Admin SDK in the
// webhook and the reply function), so the browser cannot touch it directly.
// Everything goes through the forwarded-sms-* functions, authenticated with the
// caller's Firebase ID token — the same Authorization: Bearer pattern the
// mailbox/shipping APIs use.

import { auth } from './firebase';
import { USE_SUPABASE, getAccessToken, refreshSessionOutcome, onDeadSession } from './supabaseClient';

const DELETE_FN = '/.netlify/functions/forwarded-sms-delete';
const REPLY_FN = '/.netlify/functions/forwarded-sms-reply';
const REACT_FN = '/.netlify/functions/forwarded-sms-react';

export type ForwardedDirection = 'in' | 'out';
export type ForwardedDeliveryStatus = 'queued' | 'sent' | 'failed';

export interface ForwardedSms {
  id: string;
  direction: ForwardedDirection;
  /** Thread key — the customer's number regardless of direction. */
  number: string;
  from: string;
  body: string;
  sim: string | null;
  receivedAt: number;
  routedTo: string | null;
  /** Outbound only: where the message is in its journey to the handset. */
  deliveryStatus: ForwardedDeliveryStatus | null;
  deliveryError: string | null;
  /** Dispatcher reactions, {userId: emoji}. INTERNAL — SMS can't carry them. */
  reactions: Record<string, string> | null;
  /** Reply-quote (outbound only): what this reply was written against. The
   *  quote is dispatch-side context — the customer receives only `body`. */
  quotedId: string | null;
  quotedBody: string | null;
  quotedDirection: ForwardedDirection | null;
  /** Batch id shared by photos picked & sent together (or uploaded through one
   *  link). The thread UI collages ONLY rows with the same albumId. Optional:
   *  rows from before the field (and single-photo sends) have none. */
  albumId?: string | null;
  /** OTP/shortcode text (sender id < 6 chars). Only super_admin ever receives
   *  these rows from the server; used to badge the thread as restricted. */
  otp: boolean;
  /** Set on outbound rows that are one piece of a longer message (1-based). */
  part?: number | null;
  parts?: number | null;
}

export interface ForwardedQuote {
  id: string;
  body: string;
  direction: ForwardedDirection;
}

export interface ForwardedReplyResult {
  id: string;
  deliveryStatus: ForwardedDeliveryStatus;
  error?: string;
}

/** Firebase ID-token header for dispatch-authed Netlify functions (shared by
 *  the inbox's grammar/polish calls too). */
export async function authHeader(): Promise<Record<string, string>> {
  // Supabase mode has no Firebase session — send the Supabase access token
  // instead; _email-auth routes on the token's issuer and accepts both.
  // Never throws: a broken session goes out without a bearer and the server
  // answers "Sign in required", which the caller already knows how to show.
  try {
    if (USE_SUPABASE) {
      // Refreshes first when the token is about to expire (see supabaseClient).
      const token = await getAccessToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const token = await auth?.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/**
 * fetch() for the dispatch-authed Netlify functions, with the session verdict
 * wired in (2026-09-12).
 *
 * Every one of those functions asks GoTrue whether the caller's SESSION still
 * exists before doing anything (see netlify/functions/_email-auth.js). When
 * this account is logged out from another computer — every Logout is a
 * server-wide logout — the session row is gone while the access token in this
 * tab stays cryptographically valid for up to an hour. The direct database
 * reads keep working, but every function call answers 401 "Session expired".
 * Reproduced on wolfdrivers.com: the Text Inbox kept its list, the device dot
 * aged to grey, the queue panel said "check your connection", replies failed,
 * and nothing ever sent the dispatcher to the login page.
 *
 * So a 401 here is treated as a session verdict: refresh once; if the server
 * rejects the refresh the session is dead → onDeadSession (sign out → login
 * screen); if the refresh works, retry once with the new bearer; if the
 * refresh merely could not reach the server, surface the 401 and keep the
 * session. Firestore mode is untouched — the bearer is a Firebase ID token and
 * the functions verify it themselves.
 */
export async function authedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const send = (extra: Record<string, string>) =>
    fetch(input, { ...init, headers: { ...((init.headers as Record<string, string> | undefined) ?? {}), ...extra } });
  const res = await send(await authHeader());
  if (res.status !== 401 || !USE_SUPABASE) return res;
  const { token, dead } = await refreshSessionOutcome();
  if (dead) { onDeadSession(); return res; }
  if (!token) return res; // could not reach the auth server — not a verdict
  return send({ Authorization: `Bearer ${token}` });
}

async function readError(res: Response, fallback: string): Promise<string> {
  const detail = await res.json().catch(() => ({}));
  return detail.error || `${fallback} ${res.status}`;
}

/**
 * Send a reply from the Text Inbox. The server records it in forwardedSms
 * BEFORE attempting delivery, so the returned id is already in the thread on
 * the next poll — whatever the carrier or router does afterwards.
 */
export async function sendForwardedReply(
  to: string,
  body: string,
  quote?: ForwardedQuote,
  albumId?: string,
  /** Real MMS (2026-09-04): the share token of the pictures already uploaded
   *  through sharePhoto. The server sends them as MMS attachments; `body` is
   *  the optional caption and may be ''. */
  media?: { token: string }
): Promise<ForwardedReplyResult> {
  const res = await authedFetch(REPLY_FN, {
    method: 'POST',
    // Survives the tab closing mid-request: the browser finishes the POST, the
    // server records + queues the reply, and the SIM router delivers it — no
    // open page needed from here on.
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to,
      body,
      ...(quote ? { quotedId: quote.id, quotedBody: quote.body, quotedDirection: quote.direction } : {}),
      ...(albumId ? { albumId } : {}),
      ...(media && media.token ? { mediaToken: media.token } : {}),
    }),
  });
  if (!res.ok) throw new Error(await readError(res, 'forwarded-sms-reply'));
  return res.json();
}

/** Set (emoji) or clear (null) the caller's reaction on one message. */
export async function reactForwardedSms(id: string, emoji: string | null): Promise<void> {
  const res = await authedFetch(REACT_FN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, emoji }),
  });
  if (!res.ok) throw new Error(await readError(res, 'forwarded-sms-react'));
}

export async function deleteForwardedSms(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const res = await authedFetch(DELETE_FN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(await readError(res, 'forwarded-sms-delete'));
}

